
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useLoadingState from '@/hooks/useLoadingState';
import { registerGlobalLoadingIndicator } from '@/utils/loadingIndicatorUtils';
import LoadingBar from '@/components/ui/LoadingBar';

/**
 * Global loading indicator component that tracks loading states across the application
 */
const LoadingIndicator = () => {
  const { 
    loading, 
    setLoading, 
    startRequest, 
    endRequest, 
    endAllRequests,
    timeoutRef,
    maxTimeoutRef,
    isMountedRef
  } = useLoadingState();
  const location = useLocation();

  // Expose methods to track loading state globally
  useEffect(() => {
    const cleanup = registerGlobalLoadingIndicator(
      startRequest,
      endRequest,
      endAllRequests
    );
    
    // Clear any pending loading states on component mount
    endAllRequests();
    
    return cleanup;
  }, [startRequest, endRequest, endAllRequests]);

  // Reset loading state when route changes
  useEffect(() => {
    if (!isMountedRef.current) return;
    
    // Start loading for route change
    setLoading(true);
    
    // Clear any existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
    
    // Set a new timer for hiding the loading indicator - shorter duration for better UX
    timeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      setLoading(false);
      timeoutRef.current = null;
    }, 400); // Reduced from 800ms for faster perceived loading
    
    // Automatically hide loading indicator after max timeout (failsafe)
    maxTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      console.log('Force ending loading state after max timeout');
      setLoading(false);
      maxTimeoutRef.current = null;
    }, 3000); // Reduced from 5000ms for faster failsafe
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
        maxTimeoutRef.current = null;
      }
    };
  }, [location.pathname, setLoading, isMountedRef, timeoutRef, maxTimeoutRef]);

  // Don't render anything if not loading
  if (!loading) return null;

  return <LoadingBar />;
};

export default LoadingIndicator;
